import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { X, Award } from "@/lib/icons";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PRESENTATION_RUBRIC } from "@/data/endOfYearProject";

interface SharedPayload {
  s: string;
  t: string;
  tt: string;
  sc: Record<string, number>;
  n?: string;
  at: string;
}

function decode(b64url: string): SharedPayload | null {
  try {
    const b64 = b64url.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = decodeURIComponent(escape(atob(padded)));
    const obj = JSON.parse(json);
    if (
      typeof obj?.s === "string" &&
      typeof obj?.tt === "string" &&
      obj?.sc &&
      typeof obj.sc === "object"
    ) {
      return obj as SharedPayload;
    }
    return null;
  } catch {
    return null;
  }
}

export default function SharedScoreBanner() {
  const [params, setParams] = useSearchParams();
  const [dismissed, setDismissed] = useState(false);
  const raw = params.get("score");

  const payload = useMemo(() => (raw ? decode(raw) : null), [raw]);

  useEffect(() => {
    setDismissed(false);
  }, [raw]);

  if (!raw || !payload || dismissed) return null;

  const max = PRESENTATION_RUBRIC.length * 5;
  const total = PRESENTATION_RUBRIC.reduce(
    (sum, r) => sum + (Number(payload.sc[r.label]) || 0),
    0,
  );
  const pct = Math.round((total / max) * 100);

  function dismiss() {
    setDismissed(true);
    const next = new URLSearchParams(params);
    next.delete("score");
    setParams(next, { replace: true });
  }

  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardContent className="py-4 space-y-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="rounded-md bg-primary/15 text-primary p-2 flex-shrink-0">
              <Award size={18} aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">
                Shared rubric result
              </p>
              <p className="text-base font-semibold text-foreground truncate">
                {payload.s} <span className="font-normal text-muted-foreground">- {payload.tt}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                <Badge variant="secondary" className="font-mono">
                  {total}/{max} ({pct}%)
                </Badge>
                {payload.at && (
                  <span className="ml-2 text-xs">
                    {new Date(payload.at).toLocaleString()}
                  </span>
                )}
              </p>
            </div>
          </div>
          <Button
            size="icon"
            variant="ghost"
            onClick={dismiss}
            aria-label="Dismiss shared result"
          >
            <X size={14} aria-hidden="true" />
          </Button>
        </div>

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
          {PRESENTATION_RUBRIC.map((r) => (
            <li
              key={r.label}
              className="flex items-center justify-between rounded-md border border-border bg-background/60 px-3 py-1.5"
            >
              <span className="text-foreground">{r.label}</span>
              <span className="font-mono text-muted-foreground">
                {payload.sc[r.label] ?? 0}/5
              </span>
            </li>
          ))}
        </ul>

        {payload.n && (
          <p className="text-sm text-muted-foreground italic border-l-2 border-border pl-3">
            {payload.n}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
